import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Trash2, Save, Edit2, Check } from 'lucide-react';
import { Checkbox } from './ui/checkbox';

/**
 * Unified Node Edit Modal - Used by both Visual Editor and Story Planner
 * 
 * New props:
 * - chapters: Array of chapters (for Story Planner)
 * - showChapterSelector: Show chapter dropdown (true for Story Planner, false for Visual Editor)
 * - showPlannerFields: Show Intention/Purpose/Dilemma fields
 * - currentChapterId: Current chapter context
 */
const NodeEditModal = ({ 
  node, 
  isOpen, 
  onClose, 
  onSave, 
  allNodes, 
  onCreateNode,
  // New props for Story Planner support
  chapters = [],
  showChapterSelector = false,
  showPlannerFields = false,
  currentChapterId = null
}) => {
  // Debug: log props when modal opens
  console.log('[NodeEditModal] Props:', { showChapterSelector, showPlannerFields, chapters: chapters?.length, currentChapterId });
  const [nodeName, setNodeName] = useState('');
  const [nodeBody, setNodeBody] = useState('');
  const [choices, setChoices] = useState([]);
  
  // Story Planner fields
  const [intention, setIntention] = useState('');
  const [purpose, setPurpose] = useState('');
  const [dilemma, setDilemma] = useState('');
  const [selectedChapterId, setSelectedChapterId] = useState('');
  const [prefixWithChapter, setPrefixWithChapter] = useState(false);
  
  // Track newly created nodes that need to be added to the graph
  const [newlyCreatedNodes, setNewlyCreatedNodes] = useState([]);
  
  // Track which choice is being edited for custom name
  const [editingNewName, setEditingNewName] = useState(null);
  const [tempNewName, setTempNewName] = useState('');
  
  // Extract links from body text
  const extractLinksFromBody = (body) => {
    const links = [];
    const linkRegex = /\[\[([^\]]+)\]\]/g;
    let match;
    
    while ((match = linkRegex.exec(body)) !== null) {
      const linkContent = match[1];
      
      if (linkContent.includes('->')) {
        const [label, target] = linkContent.split('->').map(s => s.trim());
        links.push({ label, target, isNew: false });
      } else if (linkContent.includes('|')) {
        const [label, target] = linkContent.split('|').map(s => s.trim());
        links.push({ label, target, isNew: false });
      } else {
        const target = linkContent.trim();
        links.push({ label: target, target, isNew: false });
      }
    }
    
    return links;
  };
  
  useEffect(() => {
    if (node) {
      setNodeName(node.name || node.title || '');
      setNodeBody(node.body || node.content || '');
      const links = extractLinksFromBody(node.body || node.content || '');
      setChoices(links);
      setNewlyCreatedNodes([]);
      setEditingNewName(null);
      
      // Story Planner fields
      setIntention(node.intention || '');
      setPurpose(node.purpose || '');
      setDilemma(node.dilemma || '');
      setSelectedChapterId(node.chapterId || currentChapterId || '');
      setPrefixWithChapter(false);
    }
  }, [node, currentChapterId]);
  
  const addChoice = () => {
    setChoices([...choices, { label: '', target: '', isNew: false }]);
  };
  
  const removeChoice = (index) => {
    const choice = choices[index];
    // If removing a newly created node, also remove it from newlyCreatedNodes
    if (choice.isNew) {
      setNewlyCreatedNodes(prev => prev.filter(n => n.id !== choice.target));
    }
    setChoices(choices.filter((_, i) => i !== index));
  };
  
  const updateChoice = (index, field, value) => {
    const updated = [...choices];
    updated[index][field] = value;
    setChoices(updated);
  };
  
  // Generate a unique node name
  const generateUniqueName = (baseName = 'NewPassage') => {
    const existingNames = new Set([
      ...allNodes.map(n => n.name),
      ...allNodes.map(n => n.id),
      ...newlyCreatedNodes.map(n => n.name),
      ...newlyCreatedNodes.map(n => n.id)
    ]);
    
    let counter = 1;
    let name = baseName;
    while (existingNames.has(name)) {
      name = `${baseName}${counter}`;
      counter++;
    }
    return name;
  };
  
  const handleCreateNewNode = (choiceIndex) => {
    const newNodeName = generateUniqueName('NewPassage');
    
    // Create the new node object
    const newNode = {
      id: newNodeName,
      name: newNodeName,
      body: 'Enter your passage content here...',
      x: 0,
      y: 0
    };
    
    // Add to newly created nodes
    setNewlyCreatedNodes(prev => [...prev, newNode]);
    
    // Update the choice to point to this new node
    const updated = [...choices];
    updated[choiceIndex].target = newNodeName;
    updated[choiceIndex].isNew = true;
    setChoices(updated);
    
    // Start editing the name immediately
    setEditingNewName(choiceIndex);
    setTempNewName(newNodeName);
  };
  
  const handleRenameNewNode = (choiceIndex) => {
    const choice = choices[choiceIndex];
    const oldName = choice.target;
    
    // Validate the new name
    if (!tempNewName.trim()) {
      setEditingNewName(null);
      return;
    }
    
    // Check if name already exists (excluding the current one being renamed)
    const existingNames = new Set([
      ...allNodes.map(n => n.name),
      ...allNodes.map(n => n.id),
      ...newlyCreatedNodes.filter(n => n.id !== oldName).map(n => n.name)
    ]);
    
    let finalName = tempNewName.trim();
    if (existingNames.has(finalName)) {
      // Make it unique
      let counter = 1;
      while (existingNames.has(`${finalName}${counter}`)) {
        counter++;
      }
      finalName = `${finalName}${counter}`;
    }
    
    // Update the newly created node's name
    setNewlyCreatedNodes(prev => prev.map(n => 
      n.id === oldName 
        ? { ...n, id: finalName, name: finalName }
        : n
    ));
    
    // Update the choice target
    const updated = [...choices];
    updated[choiceIndex].target = finalName;
    setChoices(updated);
    
    setEditingNewName(null);
    setTempNewName('');
  };
  
  const handleSave = () => {
    // Remove existing links from body
    let updatedBody = nodeBody.replace(/\[\[([^\]]+)\]\]/g, '').trim();
    
    // Add choices as links
    if (choices.length > 0) {
      updatedBody += '\n\n';
      choices.forEach(choice => {
        if (choice.label && choice.target) {
          updatedBody += `[[${choice.label}|${choice.target}]]\n`;
        }
      });
    }
    
    // Handle chapter prefix
    let finalName = nodeName;
    if (prefixWithChapter && selectedChapterId && selectedChapterId !== currentChapterId) {
      const chapter = chapters.find(c => c.id === selectedChapterId);
      if (chapter && !finalName.startsWith(chapter.title)) {
        finalName = `${chapter.title}: ${finalName}`;
      }
    }
    
    // Pass both the updated node AND any newly created nodes
    onSave(
      {
        ...node,
        name: finalName,
        title: finalName, // For Story Planner compatibility
        body: updatedBody.trim(),
        content: updatedBody.trim(), // For Story Planner compatibility
        // Story Planner fields
        intention: intention,
        purpose: purpose,
        dilemma: dilemma,
        chapterId: selectedChapterId || node.chapterId
      },
      newlyCreatedNodes // Pass the newly created nodes
    );
  };
  
  if (!node) return null;
  
  // Combine existing nodes with newly created ones for the dropdown
  const availableNodes = [
    ...allNodes.filter(n => n.id !== node.id),
    ...newlyCreatedNodes
  ];
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='max-w-3xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{node?.isChapter ? 'Edit Chapter' : 'Edit Passage'}</DialogTitle>
          <DialogDescription>
            {node?.isChapter 
              ? 'Modify the chapter details and planning information.'
              : 'Modify the passage details and add choices/links to other passages.'}
          </DialogDescription>
        </DialogHeader>
        
        <div className='space-y-4 py-4'>
          {/* Story Planner Fields - SHOW FIRST for visibility */}
          {showPlannerFields && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
              <h4 className="text-sm font-semibold text-purple-700">📋 Story Planning Fields</h4>
              
              <div>
                <Label htmlFor='intention' className="text-purple-600">Intention</Label>
                <Textarea
                  id='intention'
                  value={intention}
                  onChange={(e) => setIntention(e.target.value)}
                  placeholder='What is the intention of this scene?'
                  rows={2}
                  className="border-purple-200 focus:border-purple-400"
                />
              </div>
              
              <div>
                <Label htmlFor='purpose' className="text-purple-600">Purpose</Label>
                <Textarea
                  id='purpose'
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  placeholder='What purpose does this scene serve?'
                  rows={2}
                  className="border-purple-200 focus:border-purple-400"
                />
              </div>
              
              <div>
                <Label htmlFor='dilemma' className="text-purple-600">Dilemma</Label>
                <Textarea
                  id='dilemma'
                  value={dilemma}
                  onChange={(e) => setDilemma(e.target.value)}
                  placeholder='What dilemma does the character face?'
                  rows={2}
                  className="border-purple-200 focus:border-purple-400"
                />
              </div>
            </div>
          )}
          
          {/* Chapter Selector - Only for Story Planner */}
          {showChapterSelector && chapters.length > 0 && (
            <div>
              <Label htmlFor='chapter-select'>Chapter</Label>
              <Select value={selectedChapterId} onValueChange={setSelectedChapterId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select chapter..." />
                </SelectTrigger>
                <SelectContent>
                  {chapters.map(ch => (
                    <SelectItem key={ch.id} value={ch.id}>{ch.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedChapterId && selectedChapterId !== currentChapterId && (
                <label className="flex items-center gap-2 text-sm text-slate-600 mt-2">
                  <Checkbox
                    checked={prefixWithChapter}
                    onCheckedChange={setPrefixWithChapter}
                  />
                  Prefix title with chapter name
                </label>
              )}
            </div>
          )}
          
          {/* Chapter selector greyed out for Visual Editor */}
          {!showChapterSelector && chapters.length > 0 && (
            <div>
              <Label className="text-slate-400">Chapter (Story Planner only)</Label>
              <Input
                disabled
                value="Available in Story Planner"
                className="bg-slate-100 text-slate-400"
              />
            </div>
          )}
          
          <div>
            <Label htmlFor='passage-name'>{node?.isChapter ? 'Chapter Name' : 'Passage Name'}</Label>
            <Input
              id='passage-name'
              value={nodeName}
              onChange={(e) => setNodeName(e.target.value)}
              placeholder={node?.isChapter ? 'e.g., Chapter 1, Introduction' : 'e.g., Start, Forest, Village'}
            />
          </div>
          
          {/* Content field - HIDDEN for chapters */}
          {!node?.isChapter && (
            <div>
              <Label htmlFor='passage-body'>Content</Label>
              <Textarea
                id='passage-body'
                value={nodeBody}
                onChange={(e) => setNodeBody(e.target.value)}
                placeholder='Enter the text content of this passage...'
                rows={8}
                className='font-mono text-sm'
              />
              <p className='text-xs text-gray-500 mt-1'>
                You can use Twee formatting: **bold**, *italic*, !Heading, etc.
              </p>
            </div>
          )}
          
          {/* Choices section - HIDDEN for chapters */}
          {!node?.isChapter && (
            <div>
              <div className='flex items-center justify-between mb-2'>
                <Label>Choices (Links to other passages)</Label>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={addChoice}
                >
                  <Plus className='w-4 h-4 mr-1' />
                  Add Choice
                </Button>
              </div>
            
            <div className='space-y-3'>
              {choices.map((choice, index) => (
                <div key={index} className='flex gap-2 items-end p-3 border rounded-lg'>
                  <div className='flex-1'>
                    <Label className='text-xs'>Label</Label>
                    <Input
                      value={choice.label}
                      onChange={(e) => updateChoice(index, 'label', e.target.value)}
                      placeholder='e.g., Go left, Enter the cave'
                      size='sm'
                    />
                  </div>
                  
                  <div className='flex-1'>
                    <Label className='text-xs'>Target Passage</Label>
                    {choice.isNew && editingNewName === index ? (
                      // Show editable input for newly created passage name
                      <div className='flex gap-1'>
                        <Input
                          value={tempNewName}
                          onChange={(e) => setTempNewName(e.target.value)}
                          placeholder='Enter passage name'
                          className='flex-1'
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleRenameNewNode(index);
                            } else if (e.key === 'Escape') {
                              setEditingNewName(null);
                            }
                          }}
                        />
                        <Button
                          type='button'
                          size='sm'
                          onClick={() => handleRenameNewNode(index)}
                          className='bg-green-600 hover:bg-green-700'
                        >
                          <Check className='w-4 h-4' />
                        </Button>
                      </div>
                    ) : choice.isNew ? (
                      // Show the new passage name with edit button
                      <div className='flex gap-1'>
                        <div className='flex-1 px-3 py-2 border rounded-md bg-green-50 border-green-200 flex items-center justify-between'>
                          <span className='text-sm text-green-700 font-medium'>
                            ✨ {choice.target}
                          </span>
                          <span className='text-xs text-green-600'>(new)</span>
                        </div>
                        <Button
                          type='button'
                          variant='outline'
                          size='sm'
                          onClick={() => {
                            setEditingNewName(index);
                            setTempNewName(choice.target);
                          }}
                          title='Edit passage name'
                        >
                          <Edit2 className='w-4 h-4' />
                        </Button>
                      </div>
                    ) : (
                      // Show dropdown for existing passages
                      <Select
                        value={choice.target}
                        onValueChange={(value) => {
                          if (value === '__CREATE_NEW__') {
                            handleCreateNewNode(index);
                          } else {
                            updateChoice(index, 'target', value);
                          }
                        }}
                      >
                        <SelectTrigger className='flex-1'>
                          <SelectValue placeholder='Select or create...' />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='__CREATE_NEW__'>
                            <span className='text-green-600 font-medium'>✨ Create New Passage</span>
                          </SelectItem>
                          {availableNodes.map(n => (
                            <SelectItem key={n.id} value={n.id}>
                              {n.name}
                              {newlyCreatedNodes.some(nn => nn.id === n.id) && (
                                <span className='ml-2 text-xs text-green-600'>(new)</span>
                              )}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    onClick={() => removeChoice(index)}
                    className='text-red-500 hover:text-red-700'
                  >
                    <Trash2 className='w-4 h-4' />
                  </Button>
                </div>
              ))}
              
              {choices.length === 0 && (
                <p className='text-sm text-gray-500 italic text-center py-4'>
                  No choices yet. Add choices to link this passage to others.
                </p>
              )}
            </div>
          </div>
          )}
          
          {/* Show newly created passages summary - HIDDEN for chapters */}
          {!node?.isChapter && newlyCreatedNodes.length > 0 && (
            <div className='bg-green-50 border border-green-200 rounded-lg p-3'>
              <p className='text-sm text-green-700 font-medium mb-2'>
                📝 New passages to be created:
              </p>
              <div className='flex flex-wrap gap-2'>
                {newlyCreatedNodes.map(n => (
                  <span key={n.id} className='px-2 py-1 bg-green-100 text-green-800 rounded text-sm'>
                    {n.name}
                  </span>
                ))}
              </div>
              <p className='text-xs text-green-600 mt-2'>
                These passages will be created when you save. You can edit them after saving.
              </p>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            <Save className='w-4 h-4 mr-2' />
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NodeEditModal;
